import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  /** @see base.css — `html.dialog-scroll-lock` (not `[scroll-lock]`, which menus use). */
  static #DOCUMENT_SCROLL_LOCK_CLASS = 'dialog-scroll-lock';

  static #documentScrollLockCount = 0;

  /**
   * Page state captured by the outermost open dialog, restored by the last one to close.
   * Shared because the styles it describes are global: a dialog opened while another is
   * already open would otherwise snapshot the locked body as its "previous" state.
   *
   * @type {{ scrollY: number, pageWidth: number, position: string, top: string, width: string, applied: boolean } | null}
   */
  static #scrollLockBaseline = null;

  requiredRefs = ['dialog'];

  /** @type {number|null} */
  #openAnimationFrame = null;

  #ownsDocumentScrollLock = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    if (this.#openAnimationFrame !== null) {
      cancelAnimationFrame(this.#openAnimationFrame);
      this.#openAnimationFrame = null;
    }

    this.#restoreDocumentScroll();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #pushDocumentScrollLock() {
    if (this.#ownsDocumentScrollLock) return;

    if (DialogComponent.#documentScrollLockCount === 0) {
      DialogComponent.#scrollLockBaseline = {
        scrollY: window.scrollY,
        /* `clientWidth` matches layout width while the gutter scrollbar is still visible (Windows),
           avoiding a horizontal jump when overflow is hidden. */
        pageWidth: document.documentElement.clientWidth,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        applied: false,
      };

      document.documentElement.classList.add(DialogComponent.#DOCUMENT_SCROLL_LOCK_CLASS);
    }

    DialogComponent.#documentScrollLockCount += 1;
    this.#ownsDocumentScrollLock = true;
  }

  /** Pins the body at the captured scroll offset. Only the outermost dialog's call takes effect. */
  static #applyBodyScrollLock() {
    const baseline = DialogComponent.#scrollLockBaseline;

    if (!baseline || baseline.applied) return;

    baseline.applied = true;
    document.body.style.width = `${baseline.pageWidth}px`;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${baseline.scrollY}px`;
  }

  #restoreDocumentScroll() {
    if (!this.#ownsDocumentScrollLock) return;

    this.#ownsDocumentScrollLock = false;
    DialogComponent.#documentScrollLockCount = Math.max(0, DialogComponent.#documentScrollLockCount - 1);

    // Another dialog is still open and needs the page to stay locked.
    if (DialogComponent.#documentScrollLockCount > 0) return;

    document.documentElement.classList.remove(DialogComponent.#DOCUMENT_SCROLL_LOCK_CLASS);

    const baseline = DialogComponent.#scrollLockBaseline;
    DialogComponent.#scrollLockBaseline = null;

    if (!baseline) return;

    document.body.style.width = baseline.width;
    document.body.style.position = baseline.position;
    document.body.style.top = baseline.top;

    if (baseline.applied) window.scrollTo({ top: baseline.scrollY, behavior: 'instant' });
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#openAnimationFrame !== null) return;

    // Reads the page state it needs to restore later, before any writes happen in the frame below.
    this.#pushDocumentScrollLock();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#openAnimationFrame = requestAnimationFrame(() => {
      this.#openAnimationFrame = null;

      if (!this.isConnected) {
        this.#restoreDocumentScroll();
        return;
      }

      DialogComponent.#applyBodyScrollLock();

      try {
        dialog.showModal();
      } catch (error) {
        this.#restoreDocumentScroll();
        throw error;
      }

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (this.#openAnimationFrame !== null) {
      cancelAnimationFrame(this.#openAnimationFrame);
      this.#openAnimationFrame = null;
      this.#restoreDocumentScroll();
      return;
    }

    if (!dialog.open) {
      this.#restoreDocumentScroll();
      return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.#restoreDocumentScroll();

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
